"use client";

import { useState, useEffect } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";

const TESTIMONIALS = [
    {
        id: 1,
        name: "Sentab International",
        role: "Admin",
        company: "Sentab International",
        content: "Colombo Net Solutions is an outstanding IT service provider in Sri Lanka. Their professionalism, technical expertise, and dedication to customer satisfaction truly set them apart. From reliable IT support to innovative web and networking solutions, they consistently deliver high-quality, tailored results. I highly recommend Colombo Net Solutions for dependable and forward-thinking IT services.",
        image: "/images/hero/slide-1.webp",
        rating: 5,
    },
    {
        id: 2,
        name: "Mr. Isuru",
        role: "Admin",
        company: "D and O International",
        content: "The level of support and optimization provided by CNS is unmatched. They didn't just build a solution; they ensured it kept running at peak performance as we grew.",
        image: "/images/hero/slide-2.webp",
        rating: 5,
    },
    {
        id: 3,
        name: "Mr. Hashan Thuduhenage",
        role: "Founder",
        company: "Web Lankan.com (Pvt) Ltd.",
        content: "I would just like to pass on my thanks to everyone involved in the job well done. I have been extremely impressed by the professionalism shown by your engineers and would definitely recommend your company to others. Thank you again.",
        image: "/images/hero/slide-3.webp",
        rating: 5,
    },
    {
        id: 4,
        name: "Mr. Udana Amarasuriya",
        role: "Founder",
        company: "Amber Engineering Consortium",
        content: "Colombo Net Solution is a very easy company to work with and deliver exactly what they say they will. A good experience from start to finish",
        image: "/images/hero/slide-1.webp",
        rating: 5,
    }
];

export function Testimonials() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(1);

    // Responsive items per page
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setItemsPerPage(3);
            } else if (window.innerWidth >= 768) {
                setItemsPerPage(2);
            } else {
                setItemsPerPage(1);
            }
        };

        handleResize(); // Initial check
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % (TESTIMONIALS.length - itemsPerPage + 1));
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 < 0 ? 0 : prev - 1));
    };

    // Auto-scroll
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prev) => {
                if (prev >= TESTIMONIALS.length - itemsPerPage) return 0;
                return prev + 1;
            });
        }, 5000);
        return () => clearInterval(interval);
    }, [itemsPerPage]);

    return (
        <section className="py-24 bg-white overflow-hidden">
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <Star className="w-4 h-4 text-blue-600 fill-blue-600" />
                        <p className="text-[#566e7c] text-sm font-bold uppercase tracking-widest font-sans">
                            CLIENT TESTIMONIALS
                        </p>
                        <Star className="w-4 h-4 text-blue-600 fill-blue-600" />
                    </div>
                    <h2 className="text-3xl md:text-5xl font-bold text-[#001746] font-sans mb-6">
                        What our clients say about <span className="text-blue-600">CNS</span>
                    </h2>
                    <p className="text-[#566e7c] text-lg">
                        We take pride in delivering exceptional results. Here is what our partners have to say about their experience working with us.
                    </p>
                </div>

                {/* Slider */}
                <div className="relative">
                    <div className="overflow-hidden">
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)` }}
                        >
                            {TESTIMONIALS.map((testimonial) => (
                                <div
                                    key={testimonial.id}
                                    className={`flex-shrink-0 px-4`}
                                    style={{ width: `${100 / itemsPerPage}%` }}
                                >
                                    <div className="bg-[#eff3fa] p-8 rounded-3xl h-full flex flex-col relative group hover:bg-[#001746] hover:text-white transition-all duration-300">
                                        <Quote className="w-10 h-10 text-blue-200 mb-6 group-hover:text-blue-500 transition-colors" />

                                        <p className="text-[#001746] text-lg mb-8 italic flex-grow group-hover:text-gray-200 leading-relaxed font-sans">
                                            &quot;{testimonial.content}&quot;
                                        </p>

                                        <div className="flex items-center gap-4 mt-auto">
                                            <div className="w-12 h-12 rounded-full overflow-hidden relative border-2 border-white shadow-sm">
                                                <Image
                                                    src={testimonial.image}
                                                    alt={testimonial.name}
                                                    fill
                                                    sizes="48px"
                                                    quality={60}
                                                    className="object-cover"
                                                />
                                            </div>
                                            <div>
                                                <p className="font-bold text-[#001746] group-hover:text-white text-lg">
                                                    {testimonial.name}
                                                </p>
                                                <p className="text-[#566e7c] group-hover:text-blue-200 text-sm">
                                                    {testimonial.role}, {testimonial.company}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="flex justify-center mt-12 gap-4">
                        <button
                            type="button"
                            onClick={prevSlide}
                            className="p-3 rounded-full border border-gray-200 hover:bg-[#001746] hover:border-[#001746] hover:text-white transition-all text-[#001746] disabled:opacity-50"
                            disabled={currentIndex === 0}
                            aria-label="Previous testimonials"
                        >
                            <ChevronLeft className="w-6 h-6" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            onClick={nextSlide}
                            className="p-3 rounded-full border border-gray-200 hover:bg-[#001746] hover:border-[#001746] hover:text-white transition-all text-[#001746] disabled:opacity-50"
                            disabled={currentIndex >= TESTIMONIALS.length - itemsPerPage}
                            aria-label="Next testimonials"
                        >
                            <ChevronRight className="w-6 h-6" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
